import { useRef, useState } from 'react';
import type { EmployeePhotoResponse } from '../../api/types';
import { apiPost, apiUpload, describeError } from '../../api/client';
import { useApiResource } from '../../hooks/useApiResource';
import styles from './ProfilePhoto.module.css';
import factStyles from './CaptureSection.module.css';

const ACCEPTED_TYPES = 'image/jpeg,image/png,image/webp';

/**
 * The employee profile photo (Mission 168, "Visual Identity & Employee
 * Media" MVP, approved decision brief §1/§2). A first-class part of the
 * profile header — see `ProfileHeader.tsx` for where this mounts.
 *
 * ---------------------------------------------------------------------
 * INITIALS, NEVER A GENERIC SILHOUETTE ICON
 *
 * No photo is a completely ordinary state, not an error — the fallback
 * avatar is this person's own initials on a quiet background, never a
 * placeholder icon that reads as "something is missing."
 *
 * `viewerCanManageDocuments` DECIDES THE AFFORDANCE, NEVER A CLIENT-SIDE
 * RE-DERIVATION OF THE RULE
 *
 * The same "the API decides, the UI reads" discipline every other
 * capability hint in this profile already follows
 * (`viewerCanAssessAs`/`viewerCanRecordPotentialReadiness`) — every
 * write route re-derives this independently regardless of what this
 * flag said.
 *
 * A SHORT-LIVED URL, REFETCHED, NEVER CACHED PAST ONE PAGE VIEW
 *
 * `GET /employees/:id/photo` mints a fresh signed URL every time it is
 * called; this component never stores or reuses one across a reload.
 * ---------------------------------------------------------------------
 */
export function ProfilePhoto({
  employeeId,
  fullName,
  canManage,
}: {
  employeeId: string;
  fullName: string;
  canManage: boolean;
}) {
  const photo = useApiResource<EmployeePhotoResponse | null>(`/employees/${employeeId}/photo`);
  const [state, setState] = useState<{ status: 'idle' } | { status: 'saving' } | { status: 'failed'; message: string }>({
    status: 'idle',
  });
  const [removing, setRemoving] = useState(false);
  const [removeReason, setRemoveReason] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const initials = fullName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');

  async function handleFileChosen(file: File) {
    setState({ status: 'saving' });
    try {
      await apiUpload(`/employees/${employeeId}/photo`, file);
      setState({ status: 'idle' });
      photo.reload();
    } catch (error) {
      setState({ status: 'failed', message: describeError(error) });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleRemove() {
    if (removeReason.trim().length === 0) return;
    setState({ status: 'saving' });
    try {
      await apiPost(`/employees/${employeeId}/photo/removal`, { reason: removeReason.trim() });
      setState({ status: 'idle' });
      setRemoving(false);
      setRemoveReason('');
      photo.reload();
    } catch (error) {
      setState({ status: 'failed', message: describeError(error) });
    }
  }

  const currentUrl = photo.status === 'loaded' ? photo.data?.url : undefined;

  return (
    <div className={styles.wrap}>
      <div className={styles.avatar} aria-hidden={currentUrl ? undefined : 'true'}>
        {currentUrl ? <img className={styles.image} src={currentUrl} alt={`${fullName}'s photo`} /> : <span>{initials || '—'}</span>}
      </div>

      {canManage ? (
        <div className={styles.controls}>
          <div className={styles.controlsRow}>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_TYPES}
              className={styles.fileInput}
              id={`photo-upload-${employeeId}`}
              disabled={state.status === 'saving'}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void handleFileChosen(file);
              }}
            />
            <label htmlFor={`photo-upload-${employeeId}`} className={factStyles.editToggle}>
              {state.status === 'saving' ? 'Uploading…' : currentUrl ? 'Replace' : 'Upload photo'}
            </label>

            {currentUrl && !removing ? (
              <button type="button" className={styles.removeToggle} onClick={() => setRemoving(true)}>
                Remove
              </button>
            ) : null}
          </div>

          {removing ? (
            <div className={styles.removeForm}>
              <label htmlFor={`photo-remove-reason-${employeeId}`} className={styles.removeLabel}>
                Reason for removing this photo
              </label>
              <input
                id={`photo-remove-reason-${employeeId}`}
                className={styles.removeInput}
                value={removeReason}
                onChange={(event) => setRemoveReason(event.target.value)}
                disabled={state.status === 'saving'}
              />
              <div className={styles.removeActions}>
                <button
                  type="button"
                  className={styles.removeConfirm}
                  onClick={handleRemove}
                  disabled={state.status === 'saving' || removeReason.trim().length === 0}
                >
                  Confirm removal
                </button>
                <button
                  type="button"
                  className={factStyles.editToggle}
                  onClick={() => {
                    setRemoving(false);
                    setRemoveReason('');
                  }}
                  disabled={state.status === 'saving'}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : null}

          {state.status === 'failed' ? (
            <p className={styles.error} role="alert">
              {state.message}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
